import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { protocolosKeys } from '@/features/protocolos/hooks/useProtocolos';

import { opgApi } from '../api';
import type { Opg, OpgCreate, OpgUpdate } from '../types';

const QUERY_KEY_BASE = ['analisis-opg'] as const;

export const opgKeys = {
  all: QUERY_KEY_BASE,
  byProtocolo: (uidProtocolo: string) =>
    [...QUERY_KEY_BASE, 'by-protocolo', uidProtocolo] as const,
  detail: (uidProtocolo: string, uidAnalisis: string) =>
    [...QUERY_KEY_BASE, uidProtocolo, uidAnalisis] as const,
};

export function useOpgListByProtocolo(uidProtocolo: string | undefined) {
  return useQuery({
    queryKey: uidProtocolo ? opgKeys.byProtocolo(uidProtocolo) : opgKeys.all,
    queryFn: ({ signal }) => opgApi.listByProtocolo(uidProtocolo!, signal),
    enabled: !!uidProtocolo,
  });
}

export function useOpg(uidProtocolo: string | undefined, uidAnalisis: string | undefined) {
  return useQuery({
    queryKey:
      uidProtocolo && uidAnalisis ? opgKeys.detail(uidProtocolo, uidAnalisis) : opgKeys.all,
    queryFn: ({ signal }) => opgApi.getById(uidProtocolo!, uidAnalisis!, signal),
    enabled: !!(uidProtocolo && uidAnalisis),
  });
}

export function useCreateOpg() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ uidProtocolo, data }: { uidProtocolo: string; data: OpgCreate }) =>
      opgApi.create(uidProtocolo, data),
    onSuccess: (_data, { uidProtocolo }) => {
      void queryClient.invalidateQueries({ queryKey: opgKeys.byProtocolo(uidProtocolo) });
    },
  });
}

export function useUpdateOpg() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      uidProtocolo,
      uidAnalisis,
      data,
    }: {
      uidProtocolo: string;
      uidAnalisis: string;
      data: OpgUpdate;
    }) => opgApi.update(uidProtocolo, uidAnalisis, data),
    onSuccess: (updated: Opg, { uidProtocolo, uidAnalisis }) => {
      void queryClient.invalidateQueries({ queryKey: opgKeys.byProtocolo(uidProtocolo) });
      queryClient.setQueryData(opgKeys.detail(uidProtocolo, uidAnalisis), updated);
    },
  });
}

export function useDeleteOpg() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      uidProtocolo,
      uidAnalisis,
    }: {
      uidProtocolo: string;
      uidAnalisis: string;
    }) => opgApi.remove(uidProtocolo, uidAnalisis),
    onSuccess: (_data, { uidProtocolo }) => {
      void queryClient.invalidateQueries({ queryKey: opgKeys.byProtocolo(uidProtocolo) });
      void queryClient.invalidateQueries({ queryKey: protocolosKeys.detail(uidProtocolo) });
    },
  });
}
